'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { ItemCard } from '../../components/ItemCard';
import { cn } from '../../utils/cn';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { useCountUp } from '../../hooks/useCountUp';
import { useUiMotionEnabled } from '../../hooks/useMotion';
import { ItemPrice } from '../../components/ItemPrice';
import { List, Cell } from '../../components/ui';
import { SuperDropReveal } from '../BattleDetailsScreen/components/superDrop/SuperDropReveal';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import translations from './UpgradeScreen.translations';

const PRICE_COUNT_DELAY_MS = 400;

interface UpgradeItem {
  id: string;
  hashName: string;
  name: string;
  imageUrl: string;
  rarityColor: string;
  rarityName?: string;
  price: number;
  steamPrice?: number | null;
}

interface UpgradeSuccessResultProps {
  item: UpgradeItem;
  bigWin?: boolean;
}

export const UpgradeSuccessResult: React.FC<UpgradeSuccessResultProps> = ({ item, bigWin = false }) => {
  const isDesktop = useIsDesktop();
  const { t } = useLocalTranslation(translations);
  const motionEnabled = useUiMotionEnabled();

  const [revealing, setRevealing] = useState(bigWin && motionEnabled);
  const finishReveal = useCallback(() => setRevealing(false), []);

  const [priceTarget, setPriceTarget] = useState(motionEnabled ? 0 : item.price);
  useEffect(() => {
    if (!motionEnabled) {
      setPriceTarget(item.price);
      return;
    }
    const id = setTimeout(() => setPriceTarget(item.price), PRICE_COUNT_DELAY_MS);
    return () => clearTimeout(id);
  }, [item.price, motionEnabled]);
  const shownPrice = useCountUp(priceTarget);

  return (
    <div className={cn('w-full flex flex-col gap-4', !isDesktop && 'px-4 pt-3')}>
      {revealing && (
        <SuperDropReveal
          item={{
            hashName: item.hashName,
            name: item.name,
            imageUrl: item.imageUrl,
            rarityColor: item.rarityColor,
            price: item.price,
          }}
          title={t('bigWinTitle')}
          position="fixed"
          onDone={finishReveal}
        />
      )}

      {bigWin && (
        <div className="text-center text-[13px] leading-none font-black uppercase tracking-[4px] rtl:tracking-normal text-price">
          {t('bigWinTitle')}
        </div>
      )}

      <div className="animate-shot-result-card-in flex flex-col gap-4">
        <ItemCard
          imageUrl={item.imageUrl}
          name={item.name}
          rarity={item.rarityName}
          rarityColor={item.rarityColor}
        />
        <List>
          <Cell
            after={
              <ItemPrice credits={shownPrice} steamPrice={item.steamPrice} creditsIconSize={16} className="gap-1.5" />
            }
            isLast
          >
            {t('price')}
          </Cell>
        </List>
      </div>
    </div>
  );
};
